<template>
  <div class="text-block" :class="{ selected }">
    <div v-if="editor" class="text-toolbar">
      <v-btn-toggle dense multiple background-color="transparent" class="mr-1">
        <v-btn
          x-small
          :class="{ 'primary white--text': editor.isActive('bold') }"
          @click.stop="editor.chain().focus().toggleBold().run()"
        >
          <v-icon x-small>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn
          x-small
          :class="{ 'primary white--text': editor.isActive('italic') }"
          @click.stop="editor.chain().focus().toggleItalic().run()"
        >
          <v-icon x-small>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn
          x-small
          :class="{ 'primary white--text': editor.isActive('underline') }"
          @click.stop="editor.chain().focus().toggleUnderline().run()"
        >
          <v-icon x-small>mdi-format-underline</v-icon>
        </v-btn>
        <v-btn
          x-small
          :class="{ 'primary white--text': editor.isActive('bulletList') }"
          @click.stop="editor.chain().focus().toggleBulletList().run()"
        >
          <v-icon x-small>mdi-format-list-bulleted</v-icon>
        </v-btn>
        <v-btn
          x-small
          :class="{ 'primary white--text': editor.isActive('orderedList') }"
          @click.stop="editor.chain().focus().toggleOrderedList().run()"
        >
          <v-icon x-small>mdi-format-list-numbered</v-icon>
        </v-btn>
      </v-btn-toggle>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            v-bind="attrs"
            v-on="on"
            :class="{ 'primary white--text': editor.isActive('link') }"
            title="Link einfügen"
          >
            <v-icon x-small>mdi-link</v-icon>
            <v-icon x-small class="ml-n1">mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="onPromptLink">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-link-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>URL-Link…</v-list-item-title>
              <v-list-item-subtitle>https://…</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="openMailtoDialog">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-email-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>E-Mail-Link…</v-list-item-title>
              <v-list-item-subtitle>
                mailto mit Variablen
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-if="editor.isActive('link')" />
          <v-list-item v-if="editor.isActive('link')" @click="onUnsetLink">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-link-off</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Link entfernen</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <MailVariablePicker
        :variables="variables"
        :tenant="tenant"
        field="text"
        @insert="insertVariable"
      />
    </div>
    <editor-content
      :editor="editor"
      class="text-block-content"
      :style="contentStyle"
    />

    <MailtoLinkDialog
      :open="mailtoDialogOpen"
      :variables="variables"
      :tenant="tenant"
      :initial-href="mailtoInitialHref"
      :initial-link-text="mailtoInitialLinkText"
      :show-link-text="mailtoNeedsLinkText"
      @close="mailtoDialogOpen = false"
      @apply="onApplyMailto"
    />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Placeholder from "@tiptap/extension-placeholder";
import VariableMark from "@/components/Mail/BlockEditor/extensions/VariableMark.js";
import MailtoLinkDialog from "@/components/Mail/BlockEditor/MailtoLinkDialog.vue";
import MailVariablePicker from "@/components/Mail/MailVariablePicker.vue";
import { SUPPORT_EMAIL_MAILTO } from "@/components/Mail/templateVariables.js";
import { resolveFontSizePx } from "@/components/Mail/BlockEditor/render/fontSize.js";
import { warningVariables } from "@/components/Mail/mailVariableCatalog.js";

export default {
  name: "TextBlock",
  components: { EditorContent, MailtoLinkDialog, MailVariablePicker },
  props: {
    block: { type: Object, required: true },
    variables: { type: Array, default: () => [] },
    tenant: { type: Object, default: () => ({}) },
    selected: { type: Boolean, default: false },
  },
  data: () => ({
    editor: null,
    mailtoDialogOpen: false,
    mailtoInitialHref: SUPPORT_EMAIL_MAILTO,
    mailtoInitialLinkText: "kontaktieren",
    mailtoNeedsLinkText: true,
  }),
  computed: {
    contentStyle() {
      return {
        fontSize: `${resolveFontSizePx(this.block.fontSize)}px`,
      };
    },
    /** Warning text per conditional variable in warning level, for the chips. */
    chipWarnings() {
      const byName = {};
      warningVariables(this.variables, this.tenant).forEach(
        ({ variable, text }) => {
          byName[variable.name] = text;
        }
      );
      return byName;
    },
  },
  mounted() {
    this.editor = new Editor({
      content: this.block.html || "<p></p>",
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            rel: "noopener",
          },
        }),
        Underline,
        BulletList,
        OrderedList,
        ListItem,
        VariableMark,
        Placeholder.configure({ placeholder: "Text eingeben…" }),
      ],
      onUpdate: () => {
        this.$emit("update", { ...this.block, html: this.editor.getHTML() });
      },
    });
    this.applyChipWarnings();
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy();
  },
  watch: {
    chipWarnings() {
      this.applyChipWarnings();
    },
    "block.html"(newVal) {
      if (!this.editor) return;
      if (this.editor.getHTML() === newVal) return;
      this.editor.commands.setContent(newVal || "<p></p>", false);
    },
  },
  methods: {
    applyChipWarnings() {
      if (!this.editor) return;
      const warnings = this.chipWarnings;
      this.editor.commands.setMailVariableWarnings(
        (name) => warnings[name] || ""
      );
    },
    /** Inserts the picked expression as a chip (VariableMark). */
    insertVariable(expr, entry) {
      const label = entry.label || entry.name;
      const simpleMatch = expr.match(/^\{\{\{?\s*([\w.]+)\s*\}?\}\}$/);
      if (simpleMatch) {
        this.editor.commands.insertMailVariable(simpleMatch[1], {
          triple: expr.startsWith("{{{"),
          label,
        });
        return;
      }
      if (/^\{\{[#^]/.test(expr.trim())) {
        this.editor
          .chain()
          .focus()
          .insertContent([
            { type: "text", text: expr },
            { type: "text", text: " " },
          ])
          .run();
        return;
      }
      this.editor.commands.insertMailVariable(entry.name, { label, expr });
    },
    onPromptLink() {
      const previous = this.editor.getAttributes("link").href || "https://";
      const url = window.prompt("Link-URL eingeben (https://…)", previous);
      if (url === null) return;
      if (url === "") {
        this.onUnsetLink();
        return;
      }
      const isMailOrTel = /^(mailto|tel):/i.test(url.trim());
      this.editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({
          href: url,
          target: isMailOrTel ? null : "_blank",
        })
        .run();
    },
    openMailtoDialog() {
      const currentHref = this.editor.getAttributes("link").href || "";
      const { empty, from, to } = this.editor.state.selection;
      const selectedText = empty
        ? ""
        : this.editor.state.doc.textBetween(from, to, " ");
      this.mailtoNeedsLinkText = empty;
      this.mailtoInitialHref = /^mailto:/i.test(currentHref)
        ? currentHref
        : SUPPORT_EMAIL_MAILTO;
      this.mailtoInitialLinkText = selectedText || "kontaktieren";
      this.mailtoDialogOpen = true;
    },
    onApplyMailto({ href, linkText }) {
      const { empty } = this.editor.state.selection;
      if (empty) {
        this.editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: linkText || "kontaktieren",
            marks: [
              {
                type: "link",
                attrs: { href, target: null, rel: "noopener" },
              },
            ],
          })
          .run();
        return;
      }
      this.editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href, target: null, rel: "noopener" })
        .run();
    },
    onUnsetLink() {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
    },
  },
};
</script>

<style scoped>
.text-block {
  padding: 6px;
  border-radius: 4px;
  background: white;
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
}
.text-block.selected {
  outline: 2px solid var(--v-primary-base);
}
.text-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  padding: 4px;
  background: #f5f5f5;
  border-radius: 4px;
  max-width: 100%;
}
.text-toolbar >>> .v-btn-toggle {
  flex-wrap: wrap;
}
.text-block-content {
  min-height: 24px;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.text-block-content >>> .ProseMirror {
  outline: none;
  padding: 4px;
  min-height: 24px;
}
.text-block-content >>> .ProseMirror p {
  margin: 0 0 6px;
}
.text-block-content >>> .mail-variable-chip {
  display: inline-flex;
  align-items: center;
  background: var(--v-primary-lighten4);
  color: var(--v-primary-darken2);
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-family: inherit;
  white-space: nowrap;
  vertical-align: baseline;
}
.text-block-content >>> .mail-variable-chip--warning {
  background: var(--v-warning-lighten4);
  color: var(--v-warning-darken3);
}
.text-block-content >>> .mail-variable-chip::after {
  content: "";
}
.text-block-content >>> .mail-variable-chip[data-label]:not([data-label=""]) {
  font-size: 0;
  letter-spacing: 0;
}
.text-block-content
  >>> .mail-variable-chip[data-label]:not([data-label=""])::before {
  content: attr(data-label);
  font-size: 12px;
  font-family: inherit;
  font-weight: 500;
}
.text-block-content >>> .ProseMirror p.is-editor-empty:first-child::before {
  color: #bbb;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}
</style>
